import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, ArrowUpRight } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import { CountUp } from "./CountUp";
import { itemVariants } from "./motion";
import { MiniSparkBars } from "@/features/leads/components/LeadsStatsBar";
import { cn } from "@/lib/utils";

export type KpiTone = "primary" | "success" | "accent" | "warning";

interface KpiCardProps {
  title: string;
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  trend?: number;
  icon: ReactNode;
  tone: KpiTone;
  to?: string;
  linkTitle?: string;
  description?: string;
  bars?: number[];
  barTone?: 'emerald' | 'primary' | 'rose' | 'amber' | 'slate';
}

const TONE_BARS: Record<KpiTone, { bars: number[]; barTone: 'emerald' | 'primary' | 'rose' | 'amber' | 'slate' }> = {
  primary: { bars: [40, 65, 85, 100, 75], barTone: 'primary' },
  success: { bars: [30, 50, 70, 95, 60], barTone: 'emerald' },
  accent: { bars: [45, 60, 80, 100, 85], barTone: 'emerald' },
  warning: { bars: [90, 70, 50, 30, 20], barTone: 'amber' },
};

export function KpiCard({
  title,
  value,
  prefix,
  suffix,
  decimals,
  trend,
  icon,
  tone,
  to,
  linkTitle,
  description,
  bars,
  barTone,
}: KpiCardProps) {
  const toneCfg = TONE_BARS[tone];
  const isZero = value === 0;
  const activeBars = isZero ? [20, 20, 20, 20, 20] : (bars || toneCfg.bars);
  const activeTone = isZero ? 'slate' : (barTone || toneCfg.barTone);

  const tooltipContent = (
    <div className="w-64 space-y-1.5 p-1">
      <div className="flex items-center justify-between border-b border-slate-700/60 pb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          {title}
        </span>
        {to && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-400 hover:text-emerald-300">
            VIEW DETAILS
            <ArrowUpRight className="h-3 w-3" />
          </span>
        )}
      </div>
      {description && <p className="text-xs leading-relaxed text-slate-200">{description}</p>}
    </div>
  );

  const tileContent = (
    <div className="group/tile relative flex items-center gap-2.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-850/90 px-3 py-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 dark:hover:border-primary/40 hover:shadow-xs cursor-pointer w-full">
      {/* Left Icon Pill */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100/90 dark:bg-slate-800 text-slate-700 dark:text-slate-200 transition-all duration-200 group-hover/tile:bg-primary/10 group-hover/tile:text-primary group-hover/tile:scale-105">
        {icon}
      </div>

      {/* Right Metric Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white tabular-nums leading-tight">
            <CountUp value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
          </span>
          <MiniSparkBars bars={activeBars} tone={activeTone} />
        </div>
        <span className="inline-block text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase border-b border-dotted border-slate-300 dark:border-slate-700 group-hover/tile:text-slate-800 dark:group-hover/tile:text-slate-200 group-hover/tile:border-slate-600 transition-colors whitespace-nowrap">
          {title}
        </span>
      </div>
    </div>
  );

  return (
    <Tooltip content={tooltipContent} placement="top" maxWidth="20rem" className="block w-full">
      {to ? (
        <Link
          to={to}
          title={linkTitle ?? `View ${title}`}
          className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-xl"
        >
          {tileContent}
        </Link>
      ) : (
        tileContent
      )}
    </Tooltip>
  );
}
